import { strToU8, zipSync } from 'fflate';
import { buildFileName, getPatternMetrics } from './config';
import { buildCanvasFileName } from './canvas';
import {
  drawCanvasDocument,
  drawCanvasItemProjectionBlendZones,
  getProjectionBlendExportScale,
} from './canvasRenderer';
import { drawPattern } from './renderer';
import { buildResolumeLayout, buildResolumePresetXml, buildResolumeReadme } from './resolume';
import type { CanvasDocument, CanvasItem, GeneratorConfig } from './types';

interface WorkerResponse {
  ok: boolean;
  blob?: Blob;
  error?: string;
}

export type CanvasArchiveKind = 'outputs' | 'package' | 'resolume';

export interface CanvasExportProgress {
  completed: number;
  total: number;
  message: string;
}

export interface CanvasExportManifest {
  formatVersion: 1;
  canvas: {
    title: string;
    type: CanvasDocument['canvasType'];
    width: number;
    height: number;
    background: CanvasDocument['background'];
  };
  compositeFile: string | null;
  outputs: Array<{
    index: number;
    id: string;
    name: string;
    type: CanvasItem['kind'];
    file: string;
    canvas: { x: number; y: number; rotation: number };
    resolution: { width: number; height: number };
    physical: { widthMm: number; heightMm: number };
  }>;
  resolume?: {
    presetFile: string;
    layoutFile: string;
    readmeFile: string;
    compatibilityProfile: string;
  };
}

export async function exportPng(
  config: GeneratorConfig,
  fileName = buildFileName(config),
): Promise<void> {
  const blob = await renderPng(config);
  downloadBlob(blob, normalizeFileName(fileName, '.png'));
}

export function downloadJson(config: GeneratorConfig): void {
  const fileName = buildFileName(config).replace(/\.png$/, '.json');
  downloadBlob(new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' }), fileName);
}

export async function exportCanvasPng(
  canvasConfig: CanvasDocument,
  fileName = buildCanvasFileName(canvasConfig),
): Promise<void> {
  const blob = await renderCanvasPng(canvasConfig);
  downloadBlob(blob, normalizeFileName(fileName, '.png'));
}

export async function exportCanvasArchive(
  canvasConfig: CanvasDocument,
  fileName: string,
  kind: CanvasArchiveKind,
  onProgress?: (progress: CanvasExportProgress) => void,
): Promise<void> {
  if (canvasConfig.items.length === 0) {
    throw new Error('Add at least one Canvas item before exporting separate outputs.');
  }

  const compositeFileName = normalizeFileName(fileName, '.png');
  const includesComposite = kind === 'package' || kind === 'resolume';
  const outputFiles = canvasConfig.items.map(
    (item, index) => `outputs/${buildCanvasItemFileName(item, index)}`,
  );
  const resolumeLayout =
    kind === 'resolume' ? buildResolumeLayout(canvasConfig, outputFiles) : null;
  const total = canvasConfig.items.length + (includesComposite ? 1 : 0);
  let completed = 0;
  const files: Record<string, Uint8Array> = {};

  if (includesComposite) {
    onProgress?.({ completed, total, message: 'Rendering composite Canvas…' });
    files[compositeFileName] = await blobToBytes(await renderCanvasPng(canvasConfig));
    completed += 1;
  }

  for (const [index, item] of canvasConfig.items.entries()) {
    onProgress?.({
      completed,
      total,
      message: `Rendering output ${index + 1} of ${canvasConfig.items.length}: ${item.name}`,
    });
    files[`outputs/${buildCanvasItemFileName(item, index)}`] = await blobToBytes(
      await (item.kind === 'projection'
        ? renderCanvasItemPng(canvasConfig, item)
        : renderPng(item.config)),
    );
    completed += 1;
  }

  let resolumeManifest: CanvasExportManifest['resolume'];
  if (resolumeLayout) {
    onProgress?.({ completed, total, message: 'Building Resolume Advanced Output preset…' });
    const archiveBaseName = compositeFileName.replace(/\.png$/i, '');
    const presetFile = `resolume/${archiveBaseName}_Advanced_Output.xml`;
    const layoutFile = 'resolume/layout.json';
    const readmeFile = 'resolume/README.txt';
    files[presetFile] = strToU8(buildResolumePresetXml(resolumeLayout));
    files[layoutFile] = strToU8(JSON.stringify(resolumeLayout, null, 2));
    files[readmeFile] = strToU8(buildResolumeReadme(resolumeLayout, presetFile.split('/').pop()!));
    resolumeManifest = {
      presetFile,
      layoutFile,
      readmeFile,
      compatibilityProfile: resolumeLayout.compatibility.profile,
    };
  }

  const manifest = buildCanvasExportManifest(
    canvasConfig,
    includesComposite ? compositeFileName : null,
    resolumeManifest,
  );
  files['manifest.json'] = strToU8(JSON.stringify(manifest, null, 2));
  onProgress?.({ completed, total, message: 'Packaging ZIP archive…' });
  const archive = createZipArchive(files);
  downloadBlob(
    new Blob([toArrayBuffer(archive)], { type: 'application/zip' }),
    buildCanvasArchiveFileName(compositeFileName, kind),
  );
}

export function buildCanvasItemFileName(item: CanvasItem, index: number): string {
  const { resolution } = getPatternMetrics(item.config);
  const prefix = String(index + 1).padStart(3, '0');
  const safeName = sanitizeFileSegment(item.name) || `${item.kind}_output`;
  return `${prefix}_${safeName}_${resolution.width}x${resolution.height}.png`;
}

export function buildCanvasArchiveFileName(
  compositeFileName: string,
  suffix: CanvasArchiveKind,
): string {
  const normalized = normalizeFileName(compositeFileName, '.png');
  return `${normalized.replace(/\.png$/i, '')}_${suffix}.zip`;
}

export function buildCanvasExportRenderOptions(canvasConfig: CanvasDocument) {
  const includeProjectionBlends = canvasConfig.canvasType === 'projection';
  return {
    previewProjectionBlends: includeProjectionBlends,
    showProjectionBlendZones: includeProjectionBlends,
    projectionBlendScale: getProjectionBlendExportScale(canvasConfig.resolution),
  };
}

export function createZipArchive(files: Record<string, Uint8Array>): Uint8Array {
  return zipSync(files, { level: 0 });
}

export function buildCanvasExportManifest(
  canvasConfig: CanvasDocument,
  compositeFile: string | null,
  resolume?: CanvasExportManifest['resolume'],
): CanvasExportManifest {
  const manifest: CanvasExportManifest = {
    formatVersion: 1,
    canvas: {
      title: canvasConfig.title,
      type: canvasConfig.canvasType,
      width: canvasConfig.resolution.width,
      height: canvasConfig.resolution.height,
      background: canvasConfig.background,
    },
    compositeFile,
    outputs: canvasConfig.items.map((item, index) => {
      const metrics = getPatternMetrics(item.config);
      return {
        index: index + 1,
        id: item.id,
        name: item.name,
        type: item.kind,
        file: `outputs/${buildCanvasItemFileName(item, index)}`,
        canvas: { x: item.x, y: item.y, rotation: item.rotation },
        resolution: metrics.resolution,
        physical: metrics.physical,
      };
    }),
  };
  if (resolume) manifest.resolume = resolume;
  return manifest;
}

async function renderCanvasPng(canvasConfig: CanvasDocument): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = canvasConfig.resolution.width;
  canvas.height = canvasConfig.resolution.height;
  const context = canvas.getContext('2d', { alpha: true });
  if (!context) throw new Error('Canvas rendering is unavailable.');
  await document.fonts.ready;
  await drawCanvasDocument(context, canvasConfig, buildCanvasExportRenderOptions(canvasConfig));
  return canvasToBlob(canvas);
}

async function renderCanvasItemPng(canvasConfig: CanvasDocument, item: CanvasItem): Promise<Blob> {
  const { resolution } = getPatternMetrics(item.config);
  const canvas = document.createElement('canvas');
  canvas.width = resolution.width;
  canvas.height = resolution.height;
  const context = canvas.getContext('2d', { alpha: true });
  if (!context) throw new Error('Canvas rendering is unavailable.');
  await document.fonts.ready;
  await drawPattern(context, item.config);
  drawCanvasItemProjectionBlendZones(
    context,
    canvasConfig,
    item,
    getProjectionBlendExportScale(resolution),
  );
  return canvasToBlob(canvas);
}

export function downloadCanvasJson(canvas: CanvasDocument): void {
  const fileName = buildCanvasFileName(canvas).replace(/\.png$/, '.json');
  downloadBlob(new Blob([JSON.stringify(canvas, null, 2)], { type: 'application/json' }), fileName);
}

async function renderPng(config: GeneratorConfig): Promise<Blob> {
  if (typeof OffscreenCanvas !== 'undefined' && typeof Worker !== 'undefined') {
    try {
      return await renderInWorker(config);
    } catch {
      // Fall back for browsers with incomplete OffscreenCanvas support.
    }
  }
  return renderOnMainThread(config);
}

async function renderInWorker(config: GeneratorConfig): Promise<Blob> {
  const worker = new Worker(new URL('./export.worker.ts', import.meta.url), { type: 'module' });
  const logo = config.logo.dataUrl
    ? await createImageBitmap(await (await fetch(config.logo.dataUrl)).blob())
    : undefined;

  return new Promise<Blob>((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      worker.terminate();
      logo?.close();
      reject(new Error('PNG export timed out.'));
    }, 120_000);

    worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
      window.clearTimeout(timeout);
      worker.terminate();
      if (event.data.ok && event.data.blob) resolve(event.data.blob);
      else reject(new Error(event.data.error || 'PNG export failed.'));
    };
    worker.onerror = () => {
      window.clearTimeout(timeout);
      worker.terminate();
      logo?.close();
      reject(new Error('The background export worker failed.'));
    };
    worker.postMessage({ config, logo }, logo ? [logo] : []);
  });
}

async function renderOnMainThread(config: GeneratorConfig): Promise<Blob> {
  const { resolution } = getPatternMetrics(config);
  const canvas = document.createElement('canvas');
  canvas.width = resolution.width;
  canvas.height = resolution.height;
  const context = canvas.getContext('2d', { alpha: true });
  if (!context) throw new Error('Canvas rendering is unavailable.');
  await document.fonts.ready;
  await drawPattern(context, config);
  return canvasToBlob(canvas);
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('The browser could not encode this PNG.'));
    }, 'image/png');
  });
}

function blobToBytes(blob: Blob): Promise<Uint8Array> {
  return blob.arrayBuffer().then((buffer) => new Uint8Array(buffer));
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  return buffer;
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function normalizeFileName(fileName: string, extension: string): string {
  const sanitized =
    fileName
      .trim()
      .replace(/[<>:"/\\|?*]+/g, '_')
      .split('')
      .filter((character) => character.charCodeAt(0) > 31)
      .join('')
      .replace(/[. ]+$/g, '') || 'ScreenGrid';
  return sanitized.toLowerCase().endsWith(extension) ? sanitized : `${sanitized}${extension}`;
}

function sanitizeFileSegment(value: string): string {
  return value
    .trim()
    .replace(/[<>:"/\\|?*]+/g, '_')
    .split('')
    .filter((character) => character.charCodeAt(0) > 31)
    .join('')
    .replace(/[. ]+$/g, '')
    .replace(/[\s_]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 100);
}
